import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { Interval } from 'luxon';

import {
  getBasketSizeInsightForEntity,
  getDeliveryInsightForEntity,
  getOfflineTimeForEntity,
  getPerformanceInsightForEntityV2,
  getPreparationInsightForEntity,
  getPunctualityInsightForEntity,
  getRejectionCategoriesInsightForEntity,
  getRejectionsInsightForEntity,
  getTotalRejectedOrdersInsightForEntity,
  getTotalSalesForEntity,
  getRefundsOverviewInsightForEntity,
  getRatingsInsightForEntity,
  RefundReason,
} from 'apis/merchant-gateway/insights';
import {
  getCustomerBaseTotalsInsightForEntity,
  getCustomersByCustomerTypeInsightForEntity,
  getCustomersByOrderTypeInsightForEntity,
  getCustomersTotalInsightForEntity,
} from 'apis/merchant-gateway/insights/customers/api';

import { InsightEntity, InsightResponseWithPreviousInterval } from '../types';
import { getPrevDateInterval } from '../utils';

const withPreviousInterval =
  <T>(useQueryFunction: (insight: InsightEntity, dateInterval: Interval | null) => UseQueryResult<T>) =>
  ({
    insightEntity,
    dateInterval,
  }: {
    insightEntity: InsightEntity;
    dateInterval: Interval | null;
  }): InsightResponseWithPreviousInterval<T> => {
    const prevDateInterval = dateInterval ? getPrevDateInterval(dateInterval) : null;
    const isPreviousQueryEnabled = !!prevDateInterval;

    const selectedPeriod = useQueryFunction(insightEntity, dateInterval);
    const previousPeriodQuery = useQueryFunction(insightEntity, prevDateInterval); // we should always call query because it's a React hook

    // if previous period query is disabled then we pretend that it's loaded but empty, instead of showing error state
    const previousPeriod = isPreviousQueryEnabled
      ? previousPeriodQuery
      : {
          isError: false,
          isLoading: false,
          isFetching: false,
          data: undefined,
          refetch: undefined,
        };

    const isError = selectedPeriod.isError || previousPeriod.isError;
    const isLoading = selectedPeriod.isLoading || previousPeriod.isLoading;
    const isFetching = selectedPeriod.isFetching || previousPeriod.isFetching;
    const isDisabled = isLoading && !isFetching;

    const refetch = () => {
      selectedPeriod.isError && selectedPeriod.refetch();
      previousPeriod.isError && previousPeriod.refetch?.();
    };

    if (isDisabled) {
      // if query was disabled with `enabled: true` then it will be in `isLoading` state even if it's not fetching. let's show it as `isError` instead.
      return {
        isFetching,
        isError: true,
        isLoading: false,
        refetch,
        selected: { data: undefined },
        previous: { data: undefined },
      };
    }
    if (isError) {
      return {
        isFetching,
        isError,
        isLoading: false,
        refetch,
        selected: { data: undefined },
        previous: { data: undefined },
      };
    }
    if (isLoading) {
      return {
        isFetching,
        isError: false,
        isLoading,
        refetch,
        selected: { data: undefined },
        previous: { data: undefined },
      };
    }
    return {
      isFetching,
      isError,
      isLoading,
      refetch,
      selected: selectedPeriod,
      previous: previousPeriod,
    };
  };

const assertDateInterval: (dateInterval: Interval | null) => asserts dateInterval is Interval = dateInterval => {
  if (!dateInterval) {
    throw new Error('Request should be disabled with `enabled` option if dateInterval is empty');
  }
};

const getQueryOptions = (dateInterval: Interval | null) => ({
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  retry: false,
  enabled: !!dateInterval,
  // The insights data are calculated daily, so store each result for 12 hours
  staleTime: 12 * 60 * 60 * 1000,
});

const useExecuteQuery = <T>({
  path,
  queryFn,
  insight,
  dateInterval,
  customQueryKeys,
}: {
  path: string;
  queryFn: (dateInterval: Interval) => Promise<T>;
  insight: InsightEntity;
  dateInterval: Interval | null;
  customQueryKeys?: unknown[];
}) => {
  return useQuery(
    [path, insight.id, insight.type, dateInterval?.start, dateInterval?.end, ...(customQueryKeys ?? [])],
    () => {
      assertDateInterval(dateInterval);
      return queryFn(dateInterval);
    },
    getQueryOptions(dateInterval),
  );
};

const usePerformanceInsightV2 = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/v2/insights/performance',
    queryFn: (interval: Interval) => getPerformanceInsightForEntityV2(insight.type, insight.id, interval),
    insight,
    dateInterval,
  });

const useCustomersTotalInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/v2/insights/total-customers',
    queryFn: (interval: Interval) => getCustomersTotalInsightForEntity(insight.type, insight.id, interval),
    insight,
    dateInterval,
  });

export const useCustomerBaseTotalsInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/v2/insights/customers/total',
    queryFn: (interval: Interval) => getCustomerBaseTotalsInsightForEntity(insight.type, insight.id, interval),
    insight,
    dateInterval,
  });

export const useCustomersByOrderTypeInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/v2/insights/customers/by-subscription',
    queryFn: (interval: Interval) => getCustomersByOrderTypeInsightForEntity(insight.type, insight.id, interval),
    insight,
    dateInterval,
  });

export const useCustomersByCustomerTypeInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/v2/insights/customers/returning-vs-ftu',
    queryFn: (interval: Interval) => getCustomersByCustomerTypeInsightForEntity(insight.type, insight.id, interval),
    insight,
    dateInterval,
  });

const useRatingsInsight = (insight: InsightEntity, dateInterval: Interval | null, includeBreakdown: boolean) =>
  useExecuteQuery({
    path: '/insights/ratings',
    queryFn: (interval: Interval) => getRatingsInsightForEntity(insight.type, insight.id, interval, includeBreakdown),
    insight,
    dateInterval,
    customQueryKeys: [includeBreakdown],
  });

const useRatingsOverviewInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useRatingsInsight(insight, dateInterval, false);
const useRatingsBreakdownInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useRatingsInsight(insight, dateInterval, true);

const usePreparationInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/insights/avg-preparation-time',
    queryFn: (interval: Interval) => getPreparationInsightForEntity(insight.type, insight.id, interval),
    insight,
    dateInterval,
  });

const useTotalRejectedOrdersInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/insights/total-rejected-orders',
    queryFn: (interval: Interval) => getTotalRejectedOrdersInsightForEntity(insight.type, insight.id, interval),
    insight,
    dateInterval,
  });

export const useTotalSalesInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/insights/total-sales',
    queryFn: (interval: Interval) =>
      getTotalSalesForEntity({ entityType: insight.type, entityId: insight.id, interval }),
    insight,
    dateInterval,
  });

export const useRejectionsInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/insights/rejections',
    queryFn: (interval: Interval) =>
      getRejectionsInsightForEntity({ entityType: insight.type, entityId: insight.id, interval }),
    insight,
    dateInterval,
  });

export const usePunctualityInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/insights/punctuality',
    queryFn: (interval: Interval) =>
      getPunctualityInsightForEntity({ entityType: insight.type, entityId: insight.id, interval }),
    insight,
    dateInterval,
  });

export const useDeliveryInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/insights/delivery',
    queryFn: (interval: Interval) =>
      getDeliveryInsightForEntity({ entityType: insight.type, entityId: insight.id, interval }),
    insight,
    dateInterval,
  });

export const useRejectionCategoriesInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/insights/rejections-categories',
    queryFn: (interval: Interval) =>
      getRejectionCategoriesInsightForEntity({ entityType: insight.type, entityId: insight.id, interval, limit: 5 }),
    insight,
    dateInterval,
  });

export const useBasketSizeInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useExecuteQuery({
    path: '/insights/avg-basket-size',
    queryFn: (interval: Interval) => getBasketSizeInsightForEntity(insight.type, insight.id, interval),
    insight,
    dateInterval,
  });

export const useOfflineTimeInsight = (
  insight: InsightEntity,
  dateInterval: Interval | null,
  includeBreakdown: boolean,
) =>
  useExecuteQuery({
    path: '/insights/offline-time',
    queryFn: (interval: Interval) =>
      getOfflineTimeForEntity({ entityType: insight.type, entityId: insight.id, interval, includeBreakdown }),
    insight,
    dateInterval,
    customQueryKeys: [includeBreakdown],
  });

export const useOfflineTimeOverviewInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useOfflineTimeInsight(insight, dateInterval, false);
export const useOfflineTimeBreakdownInsight = (insight: InsightEntity, dateInterval: Interval | null) =>
  useOfflineTimeInsight(insight, dateInterval, true);

const useRefundsOverviewInsight = (
  insight: InsightEntity,
  dateInterval: Interval | null,
  refundReasons: RefundReason[],
) =>
  useExecuteQuery({
    path: '/insights/refunds-overview',
    queryFn: (interval: Interval) =>
      getRefundsOverviewInsightForEntity({ entityType: insight.type, entityId: insight.id, interval, refundReasons }),
    insight,
    dateInterval,
  });

const useRefundsOverviewInsightAll = (insight: InsightEntity, dateInterval: Interval | null) =>
  useRefundsOverviewInsight(insight, dateInterval, ['MISSED', 'WRONG']);

export const usePerformanceInsightV2WithPreviousInterval = withPreviousInterval(usePerformanceInsightV2);
export const useCustomersTotalInsightV2WithPreviousInterval = withPreviousInterval(useCustomersTotalInsight);

/**
 * @deprecated use useRatingsInsightWithPreviousInterval instead
 */
export const useRatingsOverviewInsightWithPreviousInterval = withPreviousInterval(useRatingsOverviewInsight);
export const useRatingsInsightWithPreviousInterval = withPreviousInterval(useRatingsBreakdownInsight);
export const usePreparationInsightWithPreviousInterval = withPreviousInterval(usePreparationInsight);
export const useTotalRejectedOrdersInsightWithPreviousInterval = withPreviousInterval(useTotalRejectedOrdersInsight);
export const useRejectionsInsightWithPreviousInterval = withPreviousInterval(useRejectionsInsight);
export const usePunctualityInsightWithPreviousInterval = withPreviousInterval(usePunctualityInsight);
export const useOfflineTimeOverviewInsightWithPreviousInterval = withPreviousInterval(useOfflineTimeOverviewInsight);
export const useOfflineTimeInsightWithPreviousInterval = withPreviousInterval(useOfflineTimeBreakdownInsight);
export const useRefundsOverviewInsightWithPreviousInterval = withPreviousInterval(useRefundsOverviewInsightAll);
